import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ContactSection } from "@/components/ContactSection";
import { HeroSlideshow } from "@/components/HeroSlideshow";
import { FamilySponsorship } from "@/components/FamilySponsorship";
import { ImpactStories } from "@/components/ImpactStories";
import { Button } from "@/components/ui/button";
import {
  UserPlus, Store, HeartHandshake, MapPin, ArrowRight,
  Fingerprint, Ticket, Search, CheckCircle2, Smartphone, ShieldCheck,
  Repeat, Globe2, MessageSquare, Hash, UserCheck, Printer,
  BadgeCheck, Wallet, Landmark, FileBarChart,
} from "lucide-react";

const COMMUNITY_IMG = "https://images.unsplash.com/photo-1542352526-48ef0a72c2f7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const networks = [
  { label: "Partner / Sponsor TMF", desc: "Fund measurable benefits.", to: "/signup?role=sponsor", icon: HeartHandshake,
    cls: "from-blue-600 to-blue-800", btn: "bg-white text-blue-800 hover:bg-blue-50", testid: "cta-sponsor" },
  { label: "Register as Beneficiary", desc: "Receive choice, dignity and access.", to: "/register", icon: UserPlus,
    cls: "from-amber-500 to-amber-700", btn: "bg-white text-amber-800 hover:bg-amber-50", testid: "cta-beneficiary" },
  { label: "Become a TMF Vendor", desc: "Serve beneficiaries and grow local commerce.", to: "/signup?role=vendor", icon: Store,
    cls: "from-emerald-600 to-emerald-800", btn: "bg-white text-emerald-800 hover:bg-emerald-50", testid: "cta-vendor" },
];

const infra = [
  { t: "TMF ID", icon: Fingerprint }, { t: "Digital Voucher", icon: Ticket },
  { t: "Verification", icon: BadgeCheck }, { t: "Redemption", icon: CheckCircle2 },
  { t: "Settlement", icon: Landmark }, { t: "Impact Report", icon: FileBarChart },
];

const steps = [
  { n: "01", t: "Register", d: "Provide your phone number and required information at an agent or online.", icon: UserPlus },
  { n: "02", t: "Get Your TMF ID", d: "The system creates a unique permanent Beneficiary ID.", icon: Fingerprint },
  { n: "03", t: "Receive Your Voucher", d: "An approved programme entitlement generates a unique TMF Claim Code.", icon: Ticket },
  { n: "04", t: "Find a Vendor", d: "Use the website or USSD to find an authorised TMF Vendor anywhere in Nigeria.", icon: Search },
  { n: "05", t: "Redeem", d: "Present your Claim Code and complete your redemption instantly.", icon: CheckCircle2 },
];

const channels = [
  { icon: MessageSquare, t: "SMS", d: "Receive your TMF ID, claim codes and balances by text message." },
  { icon: Hash, t: "USSD *7006*99#", d: "Redeem food, cash out, and check your benefit on any basic phone — no internet." },
  { icon: UserCheck, t: "Enrollment Agent", d: "Register and get help in person from a TMF agent in your community." },
  { icon: Printer, t: "Printed TMF Claim Voucher", d: "Carry a printed voucher with a secure code — the digital ledger stays the source of truth." },
];

const rules = [
  { icon: Repeat, t: "Partial Redemption", d: "Spend a little at a time. Unused value stays available until fully redeemed." },
  { icon: Globe2, t: "Redeem Anywhere", d: "Registered in Lagos? Redeem in Kano, Rivers or Kaduna. Your entitlement travels with you." },
  { icon: ShieldCheck, t: "One Person, One Profile", d: "A single core TMF identity — with multiple sponsor programmes attached." },
  { icon: Smartphone, t: "Web · SMS · USSD", d: "Every channel reads the same live ledger. One balance, everywhere." },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div>
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden tmf-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-14 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 tmf-rise">
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">National Digital Food & Benefit Platform</span>
            <h1 className="mt-5 font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-emerald-950">
              MAKING FOOD AND FAMILY SUPPORT <span className="tmf-gradient-text">ACCESSIBLE TO ALL.</span>
            </h1>
            <p className="mt-5 font-display font-semibold text-lg md:text-xl text-emerald-800">One Platform. Three Connected Networks.</p>
            <p className="mt-4 text-base sm:text-lg text-gray-600 max-w-xl leading-relaxed">
              TEXT-ME-FOOD connects sponsors, beneficiaries and verified local vendors through one national digital benefit platform.
            </p>
            <p className="mt-7 font-display font-bold tracking-wider text-emerald-700 text-sm">FOOD. ACCESS. DIGNITY. CHOICE.</p>
          </div>

          <div className="lg:col-span-5 tmf-rise" style={{ animationDelay: "0.15s" }}>
            <div className="relative">
              <div className="absolute -inset-4 bg-emerald-500/10 rounded-[2rem] blur-2xl" />
              <HeroSlideshow />
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl border border-emerald-100 px-5 py-4 hidden sm:block">
                <div className="text-xs text-gray-500 font-mono">TMF BENEFICIARY ID</div>
                <div className="font-mono font-bold text-emerald-700 text-sm">TMF-BNF-0000128456</div>
              </div>
            </div>
          </div>
        </div>

        {/* THREE NETWORKS */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-16 lg:pb-20 grid gap-5 md:grid-cols-3">
          {networks.map((n, i) => (
            <div key={n.label} data-testid={`network-card-${n.testid}`} className={`rounded-3xl bg-gradient-to-br ${n.cls} p-7 text-white relative overflow-hidden tmf-rise`} style={{ animationDelay: `${i * 0.08}s` }}>
              <n.icon className="w-32 h-32 absolute -right-4 -bottom-4 text-white/10" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center"><n.icon className="w-6 h-6 text-white" /></div>
                <h3 className="mt-5 font-display font-bold text-xl">{n.label}</h3>
                <p className="mt-2 text-white/85 text-sm leading-relaxed">{n.desc}</p>
                <Button data-testid={`network-btn-${n.testid}`} onClick={() => navigate(n.to)} className={`mt-6 ${n.btn} rounded-full h-11 px-5`}>Get Started <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <FamilySponsorship />

      <ImpactStories />

      {/* INFRASTRUCTURE FLOW */}
      <section className="bg-emerald-950 tmf-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 lg:py-16">
          <div className="text-center">
            <span className="text-xs font-mono uppercase tracking-widest text-emerald-300">Powered by TMF Digital Infrastructure</span>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-2 gap-y-4">
            {infra.map((s, i) => (
              <div key={s.t} className="flex items-center gap-2">
                <div data-testid={`infra-step-${i + 1}`} className="flex items-center gap-2.5 rounded-full bg-emerald-900/70 border border-emerald-700/50 px-4 py-2.5">
                  <s.icon className="w-4 h-4 text-emerald-300" />
                  <span className="text-sm font-medium text-white whitespace-nowrap">{s.t}</span>
                </div>
                {i < infra.length - 1 && <ArrowRight className="w-4 h-4 text-emerald-500 hidden sm:block" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
        <div className="max-w-2xl">
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">How TMF Works</span>
          <h2 className="mt-3 font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-emerald-950 tracking-tight">Five simple steps from registration to your first redemption.</h2>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-5">
          {steps.map((s, i) => (
            <div key={s.n} data-testid={`how-step-${s.n}`} className="group rounded-2xl border border-emerald-100 bg-white p-6 hover:shadow-lg hover:border-emerald-200 transition-all tmf-rise" style={{ animationDelay: `${i * 0.06}s` }}>
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-600 transition-colors"><s.icon className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" /></div>
                <span className="font-display font-extrabold text-2xl text-emerald-100 group-hover:text-emerald-200">{s.n}</span>
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg text-emerald-950">{s.t}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* NO SMARTPHONE */}
      <section className="bg-amber-50 border-y border-amber-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
          <div className="max-w-2xl">
            <span className="text-xs font-mono uppercase tracking-widest text-amber-700">Access for everyone</span>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-emerald-950 tracking-tight">No Smartphone? No Problem.</h2>
            <p className="mt-4 text-gray-600 text-base leading-relaxed">You don't need a smartphone to access your TMF benefit. TMF is built for rural and low-connectivity communities.</p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {channels.map((c) => (
              <div key={c.t} data-testid={`channel-${c.t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`} className="rounded-2xl bg-white border border-amber-100 p-6">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center"><c.icon className="w-5 h-5 text-amber-700" /></div>
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 ml-auto" />
                </div>
                <h3 className="mt-4 font-display font-semibold text-emerald-950">{c.t}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOLDEN RULES */}
      <section className="bg-emerald-950 tmf-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img src={COMMUNITY_IMG} alt="Community using mobile technology" className="rounded-2xl shadow-2xl object-cover w-full h-[360px] border-4 border-emerald-800/40" />
          </div>
          <div className="lg:col-span-7">
            <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">Your entitlement belongs to <span className="text-emerald-300">you</span> — not a card or a location.</h2>
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              {rules.map((r) => (
                <div key={r.t} className="flex gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-emerald-800/60 flex items-center justify-center"><r.icon className="w-5 h-5 text-emerald-300" /></div>
                  <div>
                    <h4 className="font-display font-semibold text-white">{r.t}</h4>
                    <p className="mt-1 text-sm text-emerald-200/80 leading-relaxed">{r.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SPONSOR + VENDOR CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24 grid md:grid-cols-2 gap-6">
        <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-blue-800 p-8 lg:p-10 text-white relative overflow-hidden">
          <HeartHandshake className="w-40 h-40 absolute -right-6 -bottom-6 text-white/10" />
          <h3 className="font-display font-bold text-2xl">LAUNCH YOUR IMPACT PROGRAMME</h3>
          <ul className="mt-4 space-y-1.5 text-blue-50/90 text-sm">
            <li>Reach verified beneficiaries.</li>
            <li>Deliver digital benefits.</li>
            <li>Support local businesses.</li>
            <li>Measure every redemption.</li>
          </ul>
          <Button data-testid="sponsor-cta-btn" onClick={() => navigate("/signup?role=sponsor")} className="mt-6 bg-white text-blue-800 hover:bg-blue-50 rounded-full h-12 px-6">Start a Programme <ArrowRight className="w-4 h-4 ml-2" /></Button>
        </div>
        <div className="rounded-3xl border border-emerald-200 bg-white p-8 lg:p-10 relative overflow-hidden">
          <Store className="w-40 h-40 absolute -right-6 -bottom-6 text-emerald-50/40" />
          <h3 className="font-display font-bold text-2xl text-emerald-950">Are you a TMF Vendor?</h3>
          <p className="mt-3 text-gray-600 text-sm leading-relaxed max-w-md">Verify beneficiaries. Redeem approved benefits. Track your transactions and settlements from a simple vendor terminal.</p>
          <div className="mt-6 flex gap-3">
            <Button data-testid="vendor-login-cta" onClick={() => navigate("/login")} variant="outline" className="rounded-full h-12 px-6 border-emerald-200">Vendor Login</Button>
            <Button data-testid="vendor-register-cta" onClick={() => navigate("/signup?role=vendor")} className="bg-emerald-600 hover:bg-emerald-700 rounded-full h-12 px-6">Become a Vendor</Button>
          </div>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
}
